/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { LinkPicker, type LinkPickerProps } from '@atlaskit/link-picker';
import { normalizeUrl } from '@atlaskit/linking-common/url';
import { fg } from '@atlaskit/platform-feature-flags';
import { Popup, type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { detectSourceType } from '../space-modal-form/utils';

import { type SpaceSource } from './types';

const RovoLinkPicker = React.lazy(() =>
	import(
		/* webpackChunkName: "rovo-link-picker" */
		'@atlassian/rovo-link-picker/RovoLinkPicker'
	).then((mod) => ({ default: mod.default })),
);

const messages = defineMessages({
	duplicateSourceError: {
		id: 'rovo.spaces.sourceLinkPicker.duplicateSourceError',
		defaultMessage: 'This source is already added.',
		description:
			'Error shown in the link picker footer when the user tries to add a source that is already in the space.',
	},
});

const errorContentStyles = cssMap({
	root: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		color: token('color.text.danger'),
		font: token('font.body.small'),
	},
});

const styles = cssMap({
	rovoLinkPickerPopup: {
		width: '464px',
	},
	rovoLinkPickerWrapper: {
		width: '464px',
		minHeight: '450px',
		maxHeight: '450px',
		overflowY: 'auto',
	},
});

type SourceLinkPickerPopupProps = {
	/** Rendered as the popup trigger (typically an "Add sources" button). */
	trigger: (props: TriggerProps & { onClick: () => void }) => React.ReactNode;
	/** Link picker plugins — use `useSpaceSourcePlugins(cloudId)` to create these. */
	plugins: Parameters<typeof LinkPicker>[0]['plugins'];
	/** Cloud ID passed to RovoLinkPicker when the gate is on. */
	cloudId: string;
	/** Called when the user confirms a link in the picker. */
	onSourceAdded: (source: SpaceSource) => void;
	/** The list of sources already added to the space, used to detect duplicates. */
	sources?: SpaceSource[];
};

/**
 * A `Popup`-wrapped `LinkPicker` for adding a new source to a Rovo Space.
 * Resolves a picked URL into a `SpaceSource` and calls `onSourceAdded`.
 *
 * Requires a `<SmartCardProvider>` ancestor for SmartLink resolution.
 */
export const SourceLinkPickerPopup = ({
	trigger,
	plugins,
	cloudId,
	onSourceAdded,
	sources = [],
}: SourceLinkPickerPopupProps): React.JSX.Element => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [isDuplicateError, setIsDuplicateError] = useState(false);

	const duplicateSourceErrorNode = useMemo(
		() => (
			<Box as="span" xcss={errorContentStyles.root}>
				<StatusErrorIcon label="" color="currentColor" size="small" />
				{intl.formatMessage(messages.duplicateSourceError)}
			</Box>
		),
		[intl],
	);

	// Keep stable refs so the submit handler doesn't go stale.
	const onSourceAddedRef = useRef(onSourceAdded);
	onSourceAddedRef.current = onSourceAdded;
	const sourcesRef = useRef(sources);
	sourcesRef.current = sources;

	const handleClose = useCallback(() => {
		setIsOpen(false);
		setIsDuplicateError(false);
	}, []);

	const handleLegacyCancel = useCallback(() => {
		setIsOpen(false);
	}, []);

	const handleSubmit: LinkPickerProps['onSubmit'] = useCallback(({ url, displayText, title }) => {
		// Block submission if this URL is already in the sources list.
		// Normalise both sides to catch variations like trailing slashes, protocol
		// casing, or http vs https before comparing.
		const normalizedUrl = normalizeUrl(url);
		const isDuplicate = sourcesRef.current.some((s) => normalizeUrl(s.url) === normalizedUrl);
		if (isDuplicate) {
			setIsDuplicateError(true);
			return;
		}

		setIsDuplicateError(false);
		const trimmedDisplayText = displayText?.trim() || undefined;
		const resolvedTitle = title?.trim() || undefined;
		// Only treat `displayText` as a user override when it differs from the
		// resolved title (the link picker pre-fills display-text with the title).
		const userDisplayText =
			trimmedDisplayText && trimmedDisplayText !== resolvedTitle ? trimmedDisplayText : undefined;

		onSourceAddedRef.current({
			id: `optimistic-${url}`,
			url,
			title: userDisplayText ?? resolvedTitle ?? url,
			sourceType: detectSourceType(url),
		});
		setIsOpen(false);
	}, []);

	const isRovoLinkPickerEnabled = fg('rovo_spaces_use_rovo_link_picker');

	return (
		<Popup
			isOpen={isOpen}
			onClose={handleClose}
			placement="bottom-start"
			xcss={isRovoLinkPickerEnabled ? styles.rovoLinkPickerPopup : undefined}
			zIndex={510}
			content={() =>
				isRovoLinkPickerEnabled ? (
					<Box xcss={styles.rovoLinkPickerWrapper}>
						<React.Suspense fallback={null}>
							<RovoLinkPicker
								cloudId={cloudId}
								onSubmit={handleSubmit}
								onCancel={handleClose}
								footerError={isDuplicateError ? duplicateSourceErrorNode : undefined}
							/>
						</React.Suspense>
					</Box>
				) : (
					<LinkPicker
						plugins={plugins}
						onSubmit={handleSubmit}
						onCancel={handleLegacyCancel}
						adaptiveHeight
						recentSearchListSize={3}
					/>
				)
			}
			trigger={(triggerProps) =>
				trigger({
					...triggerProps,
					onClick: () => setIsOpen((prev) => !prev),
				})
			}
		/>
	);
};
